import { AnchorHTMLAttributes, ReactNode } from 'react';
import classNames from 'classnames';
import {
  Link as BaseLink,
  LinkProps as _LinkProps,
  NavLink,
  NavLinkProps as _NavLinkProps,
} from 'react-router-dom';
import styles from './Link.module.scss';

export type LinkTypes = 'navlink' | 'link' | 'anchor';

interface INavLinkProps extends Omit<_NavLinkProps, 'type'> {
  children: ReactNode;
  className?: string;
  pure?: boolean;
  type: 'navlink';
}

interface ILinkProps extends Omit<_LinkProps, 'type'> {
  children: ReactNode;
  className?: string;
  pure?: boolean;
  type: 'link';
}

interface IAnchorProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'type' | 'href'> {
  children: ReactNode;
  className?: string;
  type: 'anchor';
  pure?: boolean;
  to: string;
}

export type LinkProps<T extends LinkTypes> = T extends 'navlink'
  ? INavLinkProps
  : T extends 'link'
  ? ILinkProps
  : T extends 'anchor'
  ? IAnchorProps
  : never;

function Link<T extends LinkTypes>({ pure, ...props }: LinkProps<T>) {
  switch (props.type ?? 'link') {
    case 'navlink':
      return (
        <NavLink
          {...(props as INavLinkProps)}
          className={classNames(pure || styles.link, props.className)}
        />
      );
    case 'anchor':
      return (
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        <a
          {...(props as IAnchorProps)}
          href={(props as IAnchorProps).to}
          className={classNames(pure || styles.link, props.className)}
        />
      );
    case 'link':
    default:
      return (
        <BaseLink
          {...(props as ILinkProps)}
          className={classNames(pure || styles.link, props.className)}
        />
      );
  }
}

export default Link;
